// @eidollon/geo/zoom-mapping
//
// Resolves a map zoom level to a semantic region level. Default thresholds
// match the master plan (docs/designs/intel-map-terrain-and-region-hierarchy.md
// §D §82): z<3 → country, z<6 → admin1, z<9 → regional, else → city.
//
// Boundary contract: LEFT-INCLUSIVE on the upper level — so z=3 resolves
// admin1 (not country), z=6 resolves regional, z=9 resolves city. NaN /
// non-finite zooms degrade to 'country' (the safest default for buggy
// upstream callers).
//
// Per-tenant overrides flow through the optional `thresholds` arg. Shape
// validated by zod at the call site so misconfigured tenant configs fail
// loudly with a clear error.
//
// Mirrors the shape of packages/intel-feed-client/src/layers/_shared.ts
// `h3ResForZoom` — the prior art for zoom→resolution mapping.

import { z } from 'zod';
import type { RegionLevel } from './types.js';

/**
 * Threshold for each level: zoom must satisfy `zoom < threshold` to fall
 * into that level (checked top-to-bottom: country, admin1, regional).
 * Anything not matched falls through to `city`.
 */
export const ZoomThresholds = z
  .object({
    country: z.number().finite(),
    admin1: z.number().finite(),
    regional: z.number().finite(),
  })
  .refine(
    (t) => t.country < t.admin1 && t.admin1 < t.regional,
    'thresholds must be strictly ascending: country < admin1 < regional',
  );
export type ZoomThresholds = z.infer<typeof ZoomThresholds>;

export const DEFAULT_THRESHOLDS: ZoomThresholds = {
  country: 3,
  admin1: 6,
  regional: 9,
};

/**
 * Map a viewport zoom to a semantic region level.
 *
 * @param zoom — current map zoom (may be fractional).
 * @param thresholds — optional per-tenant overrides; validated with zod.
 *                     Invalid (non-ascending) thresholds throw at call time.
 * @returns one of 'country' | 'admin1' | 'regional' | 'city'
 */
export function levelForZoom(
  zoom: number,
  thresholds: ZoomThresholds = DEFAULT_THRESHOLDS,
): RegionLevel {
  // Guard non-finite zoom (NaN, Infinity, -Infinity) → safest fallback.
  if (!Number.isFinite(zoom)) return 'country';

  // Validate overrides — only when caller passed a custom object. The
  // default literal is trusted (constructed in this file).
  const t = thresholds === DEFAULT_THRESHOLDS ? thresholds : ZoomThresholds.parse(thresholds);

  if (zoom < t.country) return 'country';
  if (zoom < t.admin1) return 'admin1';
  if (zoom < t.regional) return 'regional';
  return 'city';
}
